import { ChatPanelLayout } from '@/spaces/chat/panels/ChatPanelLayout';
import { useProcessActiveSwitch } from '@/spaces/chat/panels/useProcessActiveSwitch';

import type { PanelProcess } from '../../../../../bridge/types/state.ts';

interface SeededJobPanelProps {
	panelProcess: PanelProcess;
	onBack?: () => void;
	onClose: () => void;
	style?: React.CSSProperties;
	isLoading?: boolean;
	fallbackTitle?: string;
	actionRenderer?: () => React.ReactNode;
	showDescription?: boolean;
	children?: React.ReactNode;
}

// Shell for seeded "job" processes (competitor monitor, daily SMS): background
// work surfaced with title + description + the on/off status switch. Pass body
// sections via `children` (the competitor panel uses this for its
// similar-businesses / insights lists; the daily SMS panel adds its sending
// schedule). Jobs whose settings the user can edit pass `actionRenderer` for the
// footer Save button. Panels that explain themselves in their own body (the daily
// SMS panel opens with How It Works) pass showDescription={false} so the seeded
// summary is not restated under the title.
export const SeededJobPanel = ({
	panelProcess,
	onBack,
	onClose,
	style,
	isLoading = false,
	fallbackTitle = 'Job',
	actionRenderer,
	showDescription = true,
	children,
}: SeededJobPanelProps) => {
	const title =
		(panelProcess.content?.title as string | undefined) || fallbackTitle;
	const description = showDescription
		? panelProcess.user_summary || panelProcess.description || undefined
		: undefined;
	const { headerTagRender, actionFailureRender, pausedCaveatRender } =
		useProcessActiveSwitch({
			processId: panelProcess.id,
			status: panelProcess.status,
			updatedAt: panelProcess.updated_at,
			lastActionFailed: panelProcess.last_action_failed,
			pauseAriaLabel: 'Pause job',
			resumeAriaLabel: 'Resume job',
		});

	return (
		<ChatPanelLayout
			title={title}
			description={description}
			headerTagRender={headerTagRender}
			actionRenderer={actionRenderer}
			isLoading={isLoading}
			onBack={onBack}
			onClose={onClose}
			style={style}
		>
			{actionFailureRender()}
			{pausedCaveatRender()}
			{children}
		</ChatPanelLayout>
	);
};
